import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import appStore from '@/store/app';
import erpStore from '@/store/erp';
import { Amount, DoubleEntryType, SelectItem } from '@/types';
import CardComponent from '@/components/CardComponent';
import CustomToolbar from '@/components/CustomToolbar';
import { getMonthName, getCurrentMonth, getCurrentYear } from '@/filters';

@Component({
   components: { CardComponent, CustomToolbar },
})
export default class ErpAmounts extends Vue {

   @Prop({ default: true }) private readonly checkable!: boolean;

   private selected: Amount | null = null;
   private pageSize = appStore.maxPageSize;
   private pageNumber = 1;
   private year = getCurrentYear();
   private month = getCurrentMonth();

   get loading(): boolean {
      return appStore.isLoading;
   }

   get years(): SelectItem<number>[] {
      const firstYear = getCurrentYear();
      return Array.from<number, SelectItem<number>>({ length: 16 }, (x, i) => new SelectItem<number>(firstYear - i, (firstYear - i).toString()));
   }

   get months(): SelectItem<number>[] {
      return Array.from<number, SelectItem<number>>({ length: 12 }, (x, i) => new SelectItem<number>(1 + i, getMonthName(1 + i)));
   }

   get amounts(): Amount[] {
      return erpStore.amounts;
   }

   get summary(): any {
      const grandTotals: any = {
         grossAmountTotal: this.amounts.reduce((sum, amount) => this.grandTotalBySign(sum, amount.grossAmount, amount.sign), 0),
         taxesTotal: this.amounts.reduce((sum, amount) => this.grandTotalBySign(sum, amount.taxes, amount.sign), 0),
         totalAmountChargesTotal: this.amounts.reduce((sum, amount) => this.grandTotalBySign(sum, amount.totalAmountCharges, amount.sign), 0),
         netAmountTotal: this.amounts.reduce((sum, amount) => this.grandTotalBySign(sum, amount.netAmount, amount.sign), 0),
      };
      return grandTotals;
   }

   get total(): number {
      return erpStore.totalAmounts;
   }

   private async created() {
      await this.fetchAmountsAsync();
   }

   private async mounted() {
      await appStore.activateMenuById({ rootId: 300, subMenuId: 303 });
   }

   private grandTotalBySign(sum: number, value: number, sign: DoubleEntryType): number {
      return sum + (sign === DoubleEntryType.Debit ? value : value * -1);
   }

   @Watch('year', { immediate: false, deep: false })
   private async onYearChanged(value: number, previousValue?: number) {
      if (!previousValue) return;
      this.year = value;
      this.pageNumber = 1;
      await this.fetchAmountsAsync();
   }

   @Watch('month', { immediate: false, deep: false })
   private async onMonthChanged(value: number, previousValue?: number) {
      if (!previousValue) return;
      this.month = value;
      this.pageNumber = 1;
      await this.fetchAmountsAsync();
   }

   private async onPageChange(page: number) {
      this.pageNumber = page;
      await this.fetchAmountsAsync();
   }

   private async refresh() {
      await this.onPageChange(1);
   }

   // private print() {
   //    window.print();
   // }

   private async print() {
      if (this.amounts) {
         await erpStore.printAmountsAsync({ month: this.month, year: this.year })
            .then((blob) => {
               if (!blob) {
                  return;
               }
               const url = URL.createObjectURL(new Blob([blob], {
                  type: 'application/pdf',
               }));
               const link = document.createElement('a');
               link.href = url;
               link.setAttribute('download', `Cifra-${this.month}-${this.year}.pdf`);
               link.style.display = 'none';
               document.body.appendChild(link);
               link.click();
               document.body.removeChild(link);
            });
      }
   }

   private select(row: Amount, oldRow: Amount | null): void {
      this.selected = row;
   }

   private async fetchAmountsAsync() {
      await erpStore.fetchAmountsAsync({ month: this.month, year: this.year, page: this.pageNumber, size: this.pageSize });
   }
}
